import { matchPath } from "react-router";
import { PHONE_QUERY } from "../../shared/lib/useMediaQuery";
import type { Session } from "../../shared/session/session-context";
import { TOURS, WELCOME_TOUR_ID, type AudienceRole, type Localized, type TourDef, type TourStep } from "./tours";

/** A stored record (contract `GuideRecord`), reduced to what the filters need. */
export interface GuideRecordLike {
  tourId: string;
  version: number;
  status: "seen" | "dismissed";
}

/** Who is looking: flags, partner, and every role held (SystemAdmin, and project roles in any project). */
export interface GuideAudience {
  flag(name: string): boolean;
  isPartner: boolean;
  roles: ReadonlySet<AudienceRole>;
}

export function text(value: Localized): string {
  return value.th || value.en || "";
}

/**
 * The audience from the session. In test mode the session is the EFFECTIVE caller; the real person is a SystemAdmin
 * whenever `testMode` is present, so SystemAdmin tours stay listed for them (they are never auto-offered in test mode).
 */
export function audienceOf(session: Session): GuideAudience {
  const roles = new Set<AudienceRole>();
  if (session.isSystemAdmin || session.testMode) roles.add("SystemAdmin");
  for (const project of session.me.projectRoles) for (const role of project.roles) roles.add(role);
  return { flag: (name) => session.flag(name), isPartner: session.isPartner, roles };
}

/** Every tour this person may see: flag on, partners allowed, and (when the tour names roles) holding one of them. */
export function toursFor(audience: GuideAudience, tours: readonly TourDef[] = TOURS): TourDef[] {
  return tours.filter(
    (tour) =>
      (!tour.flag || audience.flag(tour.flag)) &&
      !(audience.isPartner && tour.partners === false) &&
      (!tour.roles || tour.roles.some((role) => audience.roles.has(role))),
  );
}

/** True when the tour has no page, or the path is that page. */
export function onTourPage(tour: TourDef, pathname: string): boolean {
  return !tour.page || matchPath({ path: tour.page, end: true }, pathname) !== null;
}

/** Seen or dismissed at this version or a later one. */
export function isDone(tour: TourDef, records: readonly GuideRecordLike[]): boolean {
  return records.some((record) => record.tourId === tour.id && record.version >= tour.version);
}

export const snoozeKey = (tour: TourDef) => `${tour.id}@${tour.version}`;

/** The welcome tour to start by itself, when this person has never seen it. */
export function welcomeToStart(available: readonly TourDef[], records: readonly GuideRecordLike[]): TourDef | undefined {
  const welcome = available.find((tour) => tour.id === WELCOME_TOUR_ID);
  return welcome && !isDone(welcome, records) ? welcome : undefined;
}

/**
 * The next what's-new tour to offer on this page: not done, not put off ("later") in this browser session, belonging to
 * this page (or to none), oldest release first. Nothing is offered until the welcome tour is done, so the two never stack.
 */
export function nextWhatsNew(
  available: readonly TourDef[],
  records: readonly GuideRecordLike[],
  pathname: string,
  snoozed: ReadonlySet<string>,
): TourDef | undefined {
  if (welcomeToStart(available, records)) return undefined;
  return available
    .filter((tour) => tour.kind === "whatsNew" && !isDone(tour, records) && !snoozed.has(snoozeKey(tour)) && onTourPage(tour, pathname))
    .sort((a, b) => a.releasedOn.localeCompare(b.releasedOn) || a.id.localeCompare(b.id))[0];
}

/**
 * The `data-guide` element of a step target, or null. Targets are plain ids (letters, digits, hyphens). An element may
 * carry several space-separated ids, so a tour can point at it only in some cases (a Change request's Attachments tab).
 */
export function findTarget(target: string): HTMLElement | null {
  if (!/^[a-z0-9-]+$/.test(target)) return null;
  return document.querySelector<HTMLElement>(`[data-guide~="${target}"]`);
}

/** Below 760 px (the phone layout of the shell), read live. Without matchMedia (tests) it is false. */
export function onPhone(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(PHONE_QUERY).matches;
}

/** Batch 18 §11.4: the element a step points at — its `phoneTarget` on a phone when it has one, else its `target`. */
export function stepTarget(step: TourStep, phone: boolean = onPhone()): string | undefined {
  return phone && step.phoneTarget ? step.phoneTarget : step.target;
}

/** The step's text — its `phoneBody` on a phone when it has one. */
export function stepBody(step: TourStep, phone: boolean = onPhone()): Localized {
  return phone && step.phoneBody ? step.phoneBody : step.body;
}

/** At least one step can show on the current page (a centred step always can). */
export function hasShowableStep(tour: TourDef): boolean {
  const phone = onPhone();
  return tour.steps.some((step) => {
    const target = stepTarget(step, phone);
    return !target || findTarget(target) !== null;
  });
}
